import React, { memo, useState } from 'react';

// Color scale for heatmap (from light blue to dark red)
const getHeatColor = (count, maxCount) => {
  if (count === 0 || !maxCount) return "#e5e7eb"; // Gray for no data
  
  const ratio = count / maxCount;
  
  if (ratio > 0.8) return "#dc2626"; // Red - Very high
  if (ratio > 0.6) return "#ea580c"; // Orange - High
  if (ratio > 0.4) return "#f59e0b"; // Amber - Medium-high
  if (ratio > 0.2) return "#84cc16"; // Lime - Medium
  if (ratio > 0.1) return "#22c55e"; // Green - Low-medium
  return "#3b82f6"; // Blue - Low
};

// Comunidades Autónomas with approximate positions for visual map
const COMUNIDADES_POSITIONS = {
  galicia: { name: "Galicia", row: 0, col: 0 },
  asturias: { name: "Asturias", row: 0, col: 1 },
  cantabria: { name: "Cantabria", row: 0, col: 2 },
  pais_vasco: { name: "País Vasco", row: 0, col: 3 },
  navarra: { name: "Navarra", row: 0, col: 4 },
  rioja: { name: "La Rioja", row: 1, col: 3 },
  aragon: { name: "Aragón", row: 1, col: 4 },
  cataluna: { name: "Cataluña", row: 1, col: 5 },
  castilla_leon: { name: "Castilla y León", row: 1, col: 1 },
  madrid: { name: "Madrid", row: 2, col: 2 },
  castilla_mancha: { name: "Castilla-La Mancha", row: 2, col: 3 },
  valencia: { name: "C. Valenciana", row: 2, col: 5 },
  extremadura: { name: "Extremadura", row: 2, col: 0 },
  murcia: { name: "Murcia", row: 3, col: 4 },
  andalucia: { name: "Andalucía", row: 3, col: 1 },
  baleares: { name: "Baleares", row: 2, col: 6 },
  canarias: { name: "Canarias", row: 4, col: 0 },
  ceuta: { name: "Ceuta", row: 4, col: 2 },
  melilla: { name: "Melilla", row: 4, col: 3 }
};

const SpainHeatMap = memo(({ data = [], onRegionClick }) => {
  const [hoveredRegion, setHoveredRegion] = useState(null);
  
  // Convert data array to map for quick lookup
  const dataMap = {};
  let maxCount = 0;
  
  data.forEach(item => {
    const id = item.comunidad_id || item.comunidad;
    const count = item.count || 0;
    dataMap[id] = { count, name: item.comunidad_name || id };
    if (count > maxCount) maxCount = count;
  });
  
  // Total clients
  const totalClients = data.reduce((sum, item) => sum + (item.count || 0), 0);

  // Create a grid representation
  const rows = [[], [], [], [], []];
  
  Object.entries(COMUNIDADES_POSITIONS).forEach(([id, pos]) => {
    const regionData = dataMap[id] || { count: 0, name: pos.name };
    rows[pos.row].push({
      id,
      name: pos.name,
      count: regionData.count,
      col: pos.col
    });
  });
  
  // Sort each row by column position
  rows.forEach(row => row.sort((a, b) => a.col - b.col));

  return (
    <div className="relative w-full" data-testid="spain-heatmap">
      {/* Grid Map */}
      <div className="space-y-2 p-4">
        {rows.map((row, rowIdx) => (
          <div key={rowIdx} className="flex gap-2 justify-center">
            {row.map((region) => {
              const fillColor = getHeatColor(region.count, maxCount);
              const isHovered = hoveredRegion === region.id;
              
              return (
                <button
                  key={region.id}
                  className={`
                    relative px-3 py-2 rounded-lg text-xs font-medium
                    transition-all duration-200 min-w-[100px]
                    ${isHovered ? 'scale-110 shadow-lg z-10' : 'hover:scale-105'}
                    ${region.count > 0 ? 'text-white' : 'text-gray-600'}
                  `}
                  style={{ backgroundColor: fillColor }}
                  onMouseEnter={() => setHoveredRegion(region.id)}
                  onMouseLeave={() => setHoveredRegion(null)}
                  onClick={() => onRegionClick && onRegionClick(region.id, region.name)}
                >
                  <div className="truncate">{region.name}</div>
                  <div className={`text-xs ${region.count > 0 ? 'opacity-80' : 'opacity-50'}`}>
                    {region.count} clientes
                  </div>
                  
                  {/* Tooltip on hover */}
                  {isHovered && region.count > 0 && (
                    <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white px-2 py-1 rounded text-xs whitespace-nowrap">
                      {region.name}: {region.count} clientes ({((region.count / totalClients) * 100).toFixed(1)}%)
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </div>
      
      {/* Legend */}
      <div className="flex justify-center mt-4">
        <div className="bg-white/90 backdrop-blur-sm rounded-lg p-3 shadow-md inline-flex items-center gap-4">
          <div className="text-xs font-medium text-gray-700">Densidad:</div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-3 rounded" style={{ backgroundColor: "#dc2626" }}></div>
            <span className="text-xs text-gray-500">Alta</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-3 rounded" style={{ backgroundColor: "#f59e0b" }}></div>
            <span className="text-xs text-gray-500">Media</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-3 rounded" style={{ backgroundColor: "#3b82f6" }}></div>
            <span className="text-xs text-gray-500">Baja</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-3 rounded" style={{ backgroundColor: "#e5e7eb" }}></div>
            <span className="text-xs text-gray-500">Sin datos</span>
          </div>
          <div className="ml-2 pl-2 border-l text-xs text-gray-500">
            Total: {totalClients} clientes
          </div>
        </div>
      </div>
    </div>
  );
});

SpainHeatMap.displayName = 'SpainHeatMap';

export default SpainHeatMap;
